import MainButton from '@/components/ui/custom/buttons/MainButton';
import Text from '@/components/ui/custom/dataDisplay/Text';
import SecondaryTextArea from '@/components/ui/custom/secondary/SecondaryTextArea';
import useTreePrompt from '@/data/hook/useTreePrompt';
import { restorePromptInstructionVersion } from '@/data/services/promptInstructionServices';
import { promptInstructionsQueryKeys } from '@/data/services/querykeys';
import PromptInstruction from '@/models/PromptInstruction';
import alert from '@/utils/shared/alert';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PromptInstructionHistoryWithVersion } from '../workspace/PromptsWorkspace';
import FileCard from '@/components/ui/custom/input/FileCard';
import { adapterPromptFileToAstroFile } from '@/utils/prompt';
import Label from '@/components/ui/custom/secondary/Label';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';

type PromptsVersionComparatorProps = {
  currentPrompt: PromptInstruction;
  selectedVersion: PromptInstructionHistoryWithVersion;
  onRestoreComplete: () => void;
};

export default function PromptsVersionComparator({
  currentPrompt,
  selectedVersion,
  onRestoreComplete,
}: PromptsVersionComparatorProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'prompts',
  });
  const queryClient = useQueryClient();
  const { cacheObserver } = useTreePrompt();

  const versionNumber = selectedVersion.version;

  const { mutate: restoreVersion, isPending } = useMutation({
    mutationFn: () =>
      restorePromptInstructionVersion(
        currentPrompt.id,
        selectedVersion.historyId,
      ),
    onSuccess: data => {
      cacheObserver.notifyAll(NaN, data);

      queryClient.invalidateQueries({
        queryKey: promptInstructionsQueryKeys.history(currentPrompt.id)
          .queryKey,
      });
      alert.success(t('comparator.restoreSuccess'));
      onRestoreComplete();
    },
    onError: () => {
      alert.error(t('comparator.restoreError'));
    },
  });

  return (
    <div className="flex flex-col gap-4 h-full">
      <header className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <Text
            format="poppins-600"
            text={t(`categories.${currentPrompt.category}.name`)}
            className="text-primary"
          />
          <Text
            text={t(`categories.${currentPrompt.category}.description`)}
            className="text-sm text-base-content"
          />
        </div>
      </header>

      <div className="grid grid-cols-2 gap-4 h-full">
        <div className="flex flex-col gap-4">
          <Text
            text={t('comparator.currentVersion')}
            format="rubik-500"
            className="text-info text-14"
          />

          <SecondaryTextArea
            value={currentPrompt.role || ''}
            label={{ text: t('form.roleLabel'), position: 'outside' }}
            readOnly
          />
          <SecondaryTextArea
            value={currentPrompt.instruction}
            label={{ text: t('form.instructionLabel'), position: 'outside' }}
            className={{
              div: 'h-full',
            }}
            readOnly
          />

          <ConditionalRenderer condition={currentPrompt.files?.length}>
            <div className="flex flex-col gap-1">
              <Label
                label={{ text: t('form.filesLabel'), className: 'text-14' }}
              />

              {currentPrompt.files?.map(file => (
                <FileCard
                  key={file.id}
                  file={adapterPromptFileToAstroFile(file)}
                  url={file.file}
                />
              ))}
            </div>
          </ConditionalRenderer>
        </div>

        <div className="flex flex-col gap-4 h-full">
          <div className="flex items-center justify-between">
            <Text
              text={t('comparator.selectedVersion', {
                version: `${versionNumber}.0`,
              })}
              format="rubik-500"
              className="text-info text-14"
            />
            <MainButton
              onClick={() => restoreVersion()}
              loading={isPending}
              text={t('comparator.restore')}
              color="custom"
              className="!text-primary font-600 text-14"
            />
          </div>

          <SecondaryTextArea
            value={selectedVersion.role || ''}
            label={{ text: t('form.roleLabel'), position: 'outside' }}
            readOnly
          />
          <SecondaryTextArea
            value={selectedVersion.instruction}
            label={{ text: t('form.instructionLabel'), position: 'outside' }}
            className={{
              div: 'h-full',
            }}
            readOnly
          />
          <ConditionalRenderer condition={selectedVersion.files?.length}>
            <div className="flex flex-col gap-1">
              <Label
                label={{ text: t('form.filesLabel'), className: 'text-14' }}
              />

              {selectedVersion.files?.map(file => (
                <FileCard
                  key={file.id}
                  file={adapterPromptFileToAstroFile(file)}
                  url={file.file}
                />
              ))}
            </div>
          </ConditionalRenderer>
        </div>
      </div>
    </div>
  );
}
